import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { BUCKET_NAMES, TEMP_NO_FITSCORE_POSITIONS } from "src/constants/globalConstants";
import PdfReportApi from "src/services/apis/pdfReport";
import { getTraitsMap, roundOff } from "src/utils/helperFunctions";
import FitScore from "../FitScore";
import ReportHeader from "../Header";
import BucketDetails from "./BucketDetails";
import PersonalityComponent from "../PersonalityComponent/PersonalityComponent";
import useCreatedBucketsFromReport from "./useCreatedBucketsFromReport";
import useProctoringSummary from "./useProctoringSummary";
import useQuestionLink from "./useQuestionLink";
import ProctoringReview from "./ProctoringReview/ProctoringReview";
import useProctoringTimeline from "./ProctoringReview/hooks/useProctoringTimeline";
import { readAddress, searchWith } from "./ProctoringReview/clock";
import { ReportPdfContext } from "src/utils/contexts";
import './v2.scss'
import TraitsGrid from "../TraitsGrid";
import OverallSummary from "./OverallSummary";
import CutoffTraitsTable, { getTraitsWithCutoffScores } from "./CutoffTraitsTable";
import BehaviouralAnalysis from "./BehaviouralAnalysis";
import CandidateApi from "src/services/apis/candidate";
import CandidateDetailsSection, { normalizeCandidateFormData } from "src/pages/Positions/CandidateFormData/CandidateDetailsSection";
import "src/pages/Positions/CandidateFormData/CandidateFormData.scss";
import { SectionSteps, SectionTabs, sectionsOf } from "./Report40/Sections";
import { tierOf } from "./Report40/bands";
import "./Report40/report40.scss";

const candidateFormDataRequestInFlight = new Set();

export const FUNCTIONAL_BUCKETS = Object.values(BUCKET_NAMES).filter(
  (bucket) =>
    ![
      BUCKET_NAMES.COMMUNICATION,
      /** Personality Component Buckets */
      BUCKET_NAMES.PERSONALITY_DIMENSIONS,
      BUCKET_NAMES.IPIP_LONG,
      BUCKET_NAMES.IPIP_SHORT,
      /***/
      BUCKET_NAMES.COMPETENCIES,
      BUCKET_NAMES.APTITUDE_AND_REASONING
    ].includes(bucket)
);

/** The overall score as a tile in its band's tone, beside its heading (the 4.0 report's). */
const OverallScoreBand = ({ fitScore }) => {
  const { tier, label } = tierOf({
    score: fitScore.score,
    lb: fitScore.data?.lowerBound,
    ub: fitScore.data?.upperBound,
    median: fitScore.data?.median,
  });
  return (
    <section className="r4-band" aria-label="Overall score">
      <div>
        <h4 className="single-bucket--name">Overall Score</h4>
        <p className="single-bucket--description">Overall score based on performance across all assessed skills.</p>
      </div>
      <div>
        <div className={`r4-tile r4-tile--${tier}`} title={label}>
          <b>{roundOff(fitScore.score)}</b>
          <span>Overall Score</span>
        </div>
      </div>
    </section>
  );
};

export const V2 = ({ reportData, isNonDnaReport, logMixpanelEvent }) => {

  const { reportPdfView } = useContext(ReportPdfContext);

  const [activeBucketId, setActiveBucketId] = useState(null);
  const [activeTrait, setActiveTrait] = useState(null);
  const hoverDelayRef = useRef(null);
  const [linkConfig, setLinkConfig] = useState(null)
  const [positionConfig, setPositionConfig] = useState(null)
  const [linkConfigLoaded, setLinkConfigLoaded] = useState(false)
  const [positionConfigLoaded, setPositionConfigLoaded] = useState(false)
  const [candidateFormData, setCandidateFormData] = useState(null)
  const [candidateFormDataLoading, setCandidateFormDataLoading] = useState(false)
  const { linkId, userId } = useParams();
  const { search } = useLocation();
  const history = useHistory();

  // The proctoring review room lives on this route (?view=proctoring&t=mm:ss), so
  // opening, seeking and closing it never remount the page or refetch report-data.
  const isProctoringOpen = !reportPdfView && readAddress(search).open;
  const proctoringTimeline = useProctoringTimeline({ linkId, userId, open: isProctoringOpen });
  const { userVideoSessionsList, referenceImageLink, fetchSnapshotsForReportDownload } = useProctoringSummary({
    linkId,
    linkConfig,
    positionConfig,
    reportData,
  });
  const openProctoring = () => {
    history.push({ pathname: history.location.pathname, search: searchWith(history.location.search, { view: "proctoring", t: null, item: null }) });
  };
  const closeProctoring = () => {
    history.push({ pathname: history.location.pathname, search: searchWith(history.location.search, { view: null, t: null, item: null }) });
  };

  // Driven by the company's organizationConfig.miniReport flag, which the
  // position-config response nests under organization. Managers can append the
  // undocumented ?rv=full to see the complete report instead; it only applies
  // where the mini report is configured, since everyone else already gets full.
  const isMiniReportConfigured = positionConfig?.organization?.organizationConfig?.miniReport === true;
  const isFullReportOverride = useMemo(
    () => new URLSearchParams(search).get("rv")?.toLowerCase() === "full",
    [search]
  );
  const isMiniReportEnabled = isMiniReportConfigured && !isFullReportOverride;
  // Same check as BucketDetails: with ?hideHeader=true it shows no question lists.
  const isHideHeaderForReport = useMemo(
    () => new URLSearchParams(search).get("hideHeader")?.toLowerCase() === "true",
    [search]
  );
  // Whether a question can be opened in this report (the room's "Open the question
  // in the report" link): only where the bucket drill-down lists questions.
  const canOpenQuestion = !isMiniReportEnabled && !reportPdfView && !isHideHeaderForReport;

  useEffect(() => {
    const scrolledWindow = document.querySelector(".content-wrapper");
    if (scrolledWindow) {
      scrolledWindow.scrollTo(0, 0);
    }
  }, [activeBucketId]);

  useEffect(() => {
    fetchLinkConfig()
    fetchPositionConfig()
    fetchCandidateFormData()
  }, [])

  const fetchCandidateFormData = async () => {
    if (candidateFormDataRequestInFlight.has(linkId)) return
    candidateFormDataRequestInFlight.add(linkId)
    try {
      setCandidateFormDataLoading(true)
      const response = await CandidateApi.getCandidateFormData(linkId)
      if (response.success) {
        setCandidateFormData(normalizeCandidateFormData(response.userData))
      }
    } catch (err) {
      console.error(err)
    } finally {
      setCandidateFormDataLoading(false)
      candidateFormDataRequestInFlight.delete(linkId)
    }
  }

  const fetchPositionConfig = async () => {
    try {
      const response = await PdfReportApi.getPositionConfig(linkId)
      if (response.success) {
        setPositionConfig(response.positionConfig)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setPositionConfigLoaded(true)
    }
  }

  const fetchLinkConfig = async () => {
    try {
      const response = await PdfReportApi.getLinkPositionConfig(linkId)
      if (response.success) {
        setLinkConfig(response.linkPositionConfig)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLinkConfigLoaded(true)
    }
  }

  const {
    functionalBuckets,
    communicationBuckets,
    competenciesBuckets,
    aptitudeBuckets,
    personalityBuckets,
    personalityBucketsIPIPLong,
    personalityBucketsIPIPShort,
    checkOnlyPersonalityBucket,
    renderSpecialBuckets,
    otherBuckets,
    otherBucketsWithGridView
  } = useCreatedBucketsFromReport({
    reportData,
    reportPdfView,
    setActiveBucketId,
    // Mini-report companies get the compact bucket layout on the web report too:
    // per-trait scores inline, no "View Details" drill-down.
    summaryOnly: isMiniReportEnabled,
    scoreInk: true,
  })

  const handleTraitHover = (id) => {
    if (id) {
      const refId = setTimeout(() => setActiveTrait(id), 400);
      hoverDelayRef.current = refId;
    } else {
      clearTimeout(hoverDelayRef.current);
      setActiveTrait(null);
    }
  };

  // The buckets with a "View Details" drill-down, in display order.
  const drillDownBuckets = useMemo(() => [
    ...(functionalBuckets?.buckets ?? []),
    ...(communicationBuckets?.buckets ?? []),
    ...(competenciesBuckets?.buckets ?? []),
    ...(aptitudeBuckets?.buckets ?? []),
    ...(otherBuckets ?? []),
  ], [functionalBuckets, communicationBuckets, competenciesBuckets, aptitudeBuckets, otherBuckets]);

  // Overview, then each bucket with a drill-down: the tabs over the report and the steps at a section's foot.
  const sections = useMemo(
    () => (isMiniReportEnabled || isHideHeaderForReport ? [] : sectionsOf(drillDownBuckets)),
    [drillDownBuckets, isMiniReportEnabled, isHideHeaderForReport]
  );
  const openSection = (id) => {
    clearFocus();
    setActiveBucketId(id);
  };
  const showSkillScores = () => {
    const scrollTo = () => document.getElementById("r4-cutoffs")?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (activeBucketId) {
      openSection(null);
      setTimeout(scrollTo, 0);
    } else scrollTo();
  };

  const activeBucketData = activeBucketId
    ? drillDownBuckets.find(({ id }) => id === activeBucketId)
    : null;

  // ?question=<id> (the room's link to a question): open its bucket and scroll to it.
  const { focusQuestionId, clearFocus } = useQuestionLink({
    linkId,
    buckets: drillDownBuckets,
    ready: !!reportData && positionConfigLoaded,
    roomOpen: isProctoringOpen,
    canOpen: canOpenQuestion,
    openBucket: setActiveBucketId,
  });

  const hideFitScore = useMemo(() => {
    if (!positionConfig?.positionId) return true;

    const { positionId } = positionConfig;
    const hiddenFitScore = TEMP_NO_FITSCORE_POSITIONS.includes(positionId);

    return hiddenFitScore || checkOnlyPersonalityBucket;
  }, [positionConfig, reportData?.buckets]);

  return (
    <div id="one-page-report-component" className="v2 report-40">
      <div className="info v2">
        <div className="content-wrapper">
          <div className="max-width-left">
            <ReportHeader
              layout="profile"
              onSkillScores={getTraitsWithCutoffScores(reportData).length ? showSkillScores : undefined}
              hideCta
              reportData={reportData}
              linkConfig={linkConfig}
              positionConfig={positionConfig}
              userVideoSessionsList={userVideoSessionsList}
              isNonDnaReport={isNonDnaReport}
              logMixpanelEvent={logMixpanelEvent}
              showProctoringModal={() => openProctoring()}
              fetchSnapshotsForReportDownload={fetchSnapshotsForReportDownload}
              referenceImageLink={referenceImageLink}
              hideFitScore={hideFitScore}
              companyId={reportData?.position?.organizationId}
              hideVerdictPills={reportData?.cutOffCleared != null}
              candidateFormData={candidateFormData}
            />
            <div className="report-candidate-section">
              <CandidateDetailsSection
                formData={candidateFormData}
                loading={candidateFormDataLoading}
                hideWhenEmpty
                reportView
                collapsible
                defaultCollapsed
              />
            </div>
            {sections.length > 1 && (
              <SectionTabs sections={sections} activeId={activeBucketId} onSelect={openSection} />
            )}
            {!activeBucketId ? (
              <div className="r4-main">
                {
                  reportData?.cutOffCleared != null ? (
                    <>
                      <section className="r4-outcome" id="r4-cutoffs" aria-label="Overall summary">
                        <OverallSummary reportData={reportData} positionConfig={positionConfig} soft />
                        <CutoffTraitsTable reportData={reportData} soft />
                      </section>
                      {reportData?.fitScore && reportData?.fitScore?.cutOff == null && (
                        <OverallScoreBand fitScore={reportData.fitScore} />
                      )}
                    </>
                  ) : hideFitScore ? null : (
                    <FitScore
                      {...reportData?.fitScore}
                      dnaScore={null}
                      showLegends={isNonDnaReport}
                      fitScoreMultiplier={positionConfig?.fitScoreMultiplier ?? 1}
                      hideIndustryAverage={personalityBuckets.enableBucketAnalysis?.personalityBucket && personalityBuckets.bucketAnalysis?.personalityBucket}
                    />
                  )
                }
                <BehaviouralAnalysis behaviouralAnalysis={reportData?.behaviouralAnalysis} />
                {!!functionalBuckets.buckets?.length > 0 && (
                  <div className="single-bucket">
                    <div>
                      <h4 className="single-bucket--name">
                        {functionalBuckets.name}
                      </h4>
                      <p className="single-bucket--description">
                        {functionalBuckets.description}
                      </p>
                    </div>
                    <div>
                      {functionalBuckets.buckets.map((bucket) =>
                        renderSpecialBuckets(bucket)
                      )}
                    </div>
                  </div>
                )}
                {!!communicationBuckets.buckets?.length > 0 && (
                  <div className="single-bucket">
                    <div>
                      <h4 className="single-bucket--name">
                        {communicationBuckets.name}
                      </h4>
                      <p className="single-bucket--description">
                        {communicationBuckets.description}
                      </p>
                    </div>
                    <div>
                      {communicationBuckets.buckets.map((bucket) =>
                        renderSpecialBuckets(bucket)
                      )}
                    </div>
                  </div>
                )}
                {!!competenciesBuckets.buckets?.length > 0 && (
                  <div className="single-bucket">
                    <div>
                      <h4 className="single-bucket--name">
                        {competenciesBuckets.name}
                      </h4>
                      <p className="single-bucket--description">
                        {competenciesBuckets.description}
                      </p>
                    </div>
                    <div>
                      {competenciesBuckets.buckets.map((bucket) =>
                        renderSpecialBuckets(bucket)
                      )}
                    </div>
                  </div>
                )}
                {!!aptitudeBuckets.buckets?.length > 0 && (
                  <div className="single-bucket">
                    <div>
                      <h4 className="single-bucket--name">
                        {aptitudeBuckets.name}
                      </h4>
                      <p className="single-bucket--description">
                        {aptitudeBuckets.description}
                      </p>
                    </div>
                    <div>
                      {aptitudeBuckets.buckets.map((bucket) =>
                        renderSpecialBuckets(bucket)
                      )}
                    </div>
                  </div>
                )}
                {!!otherBuckets?.length > 0 && (
                  otherBuckets.map((bucket) => (
                    <div key={bucket.id} className="single-bucket">
                      <div>
                        <h4 className="single-bucket--name">
                          {bucket.name}
                        </h4>
                        {bucket.description && (
                          <p className="single-bucket--description">
                            {bucket.description}
                          </p>
                        )}
                      </div>
                      <div>
                        {renderSpecialBuckets(bucket)}
                      </div>
                    </div>
                  ))
                )}
                {!!reportData &&
                  otherBucketsWithGridView?.map(
                    ({ id, name, description, traitsMeta }) =>
                      !Object.values(BUCKET_NAMES).includes(name) ? (
                        <div key={id} className="group-container">
                          <TraitsGrid
                            showBucketWise
                            hideLegends
                            title={name}
                            description={description}
                            traitsMap={getTraitsMap([{ traitsMeta }])}
                            activeTrait={activeTrait}
                            onTraitHover={handleTraitHover}
                          />
                        </div>
                      ) : null
                  )}
                {!!personalityBuckets.traits?.length > 0 &&
                  <PersonalityComponent
                    bucket={personalityBuckets}
                  />
                }
                {!!personalityBucketsIPIPLong.traits?.length > 0 &&
                  <PersonalityComponent
                    bucket={personalityBucketsIPIPLong}
                  />
                }
                {!!personalityBucketsIPIPShort.traits?.length > 0 &&
                  <PersonalityComponent
                    bucket={personalityBucketsIPIPShort}
                  />
                }
              </div>
            ) : (
              activeBucketData && (
                <BucketDetails
                  // A fresh instance per bucket: a question link can switch buckets in place.
                  key={activeBucketData.id}
                  {...activeBucketData}
                  reportData={reportData}
                  companyId={reportData?.position?.organizationId}
                  headerTraitColor={"grey"}
                  focusQuestionId={focusQuestionId}
                  onQuestionFocused={clearFocus}
                  onBack={() => openSection(null)}
                  filterable
                  footer={<SectionSteps sections={sections} activeId={activeBucketData.id} onSelect={openSection} />}
                  audioProctoring={positionConfig?.audioProctoring}
                  plagiarismConfig={{
                    plagiarism: positionConfig?.plagiarism,
                    maxAllowed: positionConfig?.maxAllowedPlagiarism,
                    minAllowed: positionConfig?.minAllowedPlagiarism
                  }}
                />
              )
            )}
          </div>
        </div>
      </div>
      {isProctoringOpen && (
        <ProctoringReview
          reportData={reportData}
          linkConfig={linkConfig}
          positionConfig={positionConfig}
          configsReady={linkConfigLoaded && positionConfigLoaded}
          timelineState={proctoringTimeline}
          canOpenQuestion={canOpenQuestion}
          onClose={closeProctoring}
        />
      )}
    </div>
  );
};
